import { Button } from "components/Button/Button"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { useNotification } from "components/Notification/Notification"
import { Step, Visit } from "interface/visit"
import { useUpdateVisit } from "services/visits"
import { asDate } from "utils/date"
import { useStepCompletion } from "./useStepCompletion"

export function MarkAsComplete({
	visit,
	steps = [
		"linkFootprint",
		"softwareUpdate",
		"testScan",
		"sendLink",
		"imageUpload",
	],
	onSuccess,
}: {
	visit: Visit
	steps?: Step[]
	onSuccess: () => void
}) {
	const { notify } = useNotification()
	const {
		mutate: completeVisit,
		isError,
		isPending,
	} = useUpdateVisit(visit.id, true)
	const { isFullyComplete } = useStepCompletion(visit)
	const isComplete = isFullyComplete(...steps)
	const handleCompleteVisit = () => {
		completeVisit(
			{
				status: "COMPLETED",
				completedDate: asDate().toISOString(),
			},
			{
				onSuccess: () => {
					notify.success({
						message: "Mark entire visit as complete",
						description: "Visit is completed",
					})
					onSuccess()
				},
			},
		)
	}

	return (
		<div className="mx-auto bg-white px-6 py-4">
			{visit?.status === "COMPLETED" && (
				<p className="text-lg font-semibold">Visit is completed</p>
			)}

			{visit?.status !== "COMPLETED" && (
				<div className="flex max-w-3xl flex-col items-center gap-4">
					<p className="text-center">
						By clicking the button below, you are confirming that all actions on
						your visit checklist have been completed. You will no longer be able
						to access this page once the visit is marked as complete.
					</p>

					<div className="flex w-full max-w-lg flex-col gap-2">
						<Button
							type="primary"
							size="large"
							htmlType="button"
							loading={isPending}
							disabled={!isComplete}
							className="w-full"
							onClick={handleCompleteVisit}
						>
							Mark entire visit as complete
						</Button>

						<ErrorStatus show={isError && !isPending}>
							Failed to mark entire visit as complete. Please, try again.
						</ErrorStatus>
					</div>
				</div>
			)}
		</div>
	)
}
